import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { listAuditEvents, type AuditEvent } from '../api/audit';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { Modal } from '../components/Modal';
import { Icon } from '../lib/icons';

type SortOrder = 'newest' | 'oldest';

const PAGE_SIZE = 50;

/**
 * FEAT-013 — the Security Log.
 *
 * Laid out as the prototype has it: the same page head, the same card, and the
 * same three columns — time, actor, event. Two things the prototype could not
 * have, because its log was reseeded on every refresh and never left the
 * browser:
 *
 *   - **The date, not just the clock time.** A log that spans months cannot say
 *     "10:42" and leave the reader to guess which day.
 *   - **Paging.** The list grows without bound, so it cannot all be fetched.
 *
 * The remaining fields — target, IP, user agent — open in a dialog rather than
 * becoming four more columns. They are what you want once you have found the
 * entry, not what you scan for while looking.
 */
export function SecurityPage(): ReactElement {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOrder>('newest');
  const [viewing, setViewing] = useState<AuditEvent | null>(null);

  const query = useQuery({
    queryKey: ['audit', page, from, to, search, sort],
    queryFn: () =>
      listAuditEvents({
        page,
        pageSize: PAGE_SIZE,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(search ? { q: search } : {}),
        sort,
      }),
    /*
     * The previous page stays on screen while the next one loads, so paging
     * does not blank the table and jump the scroll position back to the top.
     */
    placeholderData: (previous) => previous,
  });

  /** Changing a filter invalidates the page number — page 7 of a new filter is meaningless. */
  function filter(next: { from?: string; to?: string; q?: string; sort?: SortOrder }): void {
    if (next.from !== undefined) setFrom(next.from);
    if (next.to !== undefined) setTo(next.to);
    if (next.q !== undefined) setSearch(next.q);
    if (next.sort !== undefined) setSort(next.sort);
    setPage(1);
  }

  const meta = query.data?.meta;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_admin')}</div>
          <h1>{t('security')}</h1>
          <div className="lead">{t('sec_only')}</div>
        </div>
      </div>

      <div className="note">
        <Icon name="shield" />
        <div>{t('sec_note')}</div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('security')}</h2>
          <span className="hint">{meta?.total_items ?? 0}</span>
        </div>

        <div className="filterbar">
          {/*
            First, because it is what somebody actually arrives wanting: the log
            is append-only and grows without limit, so paging through it to find
            "who deleted that room" is reading, not searching.
          */}
          <div className="fb-item">
            <label htmlFor="sec-q">{t('sec_search')}</label>
            <input
              id="sec-q"
              className="fb-input fb-search"
              type="search"
              placeholder={t('sec_search_ph')}
              value={search}
              onChange={(e) => filter({ q: e.target.value })}
            />
          </div>

          <div className="fb-item">
            <label htmlFor="sec-sort">{t('sec_sort')}</label>
            <select
              id="sec-sort"
              className="fb-input"
              value={sort}
              onChange={(e) => filter({ sort: e.target.value as SortOrder })}
            >
              <option value="newest">{t('sec_newest')}</option>
              <option value="oldest">{t('sec_oldest')}</option>
            </select>
          </div>

          <div className="fb-item">
            <label htmlFor="sec-from">{t('vs_from')}</label>
            <input
              id="sec-from"
              className="fb-input"
              type="date"
              value={from}
              onChange={(e) => filter({ from: e.target.value })}
            />
          </div>

          <div className="fb-item">
            <label htmlFor="sec-to">{t('vs_to')}</label>
            <input
              id="sec-to"
              className="fb-input"
              type="date"
              value={to}
              onChange={(e) => filter({ to: e.target.value })}
            />
          </div>

          <div className="fb-actions">
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setFrom('');
                setTo('');
                setSearch('');
                setSort('newest');
                setPage(1);
              }}
            >
              <Icon name="refresh" />
              {t('vs_clear')}
            </button>
          </div>
        </div>

        {query.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : query.error ? (
          <div className="empty" role="alert">
            {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
          </div>
        ) : query.data && query.data.items.length === 0 ? (
          <div className="empty">
            <div className="em-ic">
              <Icon name="shield" />
            </div>
            {t('none_yet')}
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('sec_time')}</th>
                    <th scope="col">{t('sec_actor')}</th>
                    <th scope="col">{t('sec_event')}</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data?.items.map((entry) => (
                    <ClickableRow key={entry.id} onOpen={() => setViewing(entry)}>
                      <td className="num">
                        <Occurred at={entry.occurred_at} />
                      </td>
                      <td>
                        <RowTitle onOpen={() => setViewing(entry)}>
                          <Actor entry={entry} />
                        </RowTitle>
                      </td>
                      <td>{entry.event}</td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.total_pages > 1 ? (
              <Pager
                page={meta.page}
                totalPages={meta.total_pages}
                busy={query.isFetching}
                onGo={setPage}
              />
            ) : null}
          </>
        )}
      </div>

      {viewing ? <EventDialog entry={viewing} onClose={() => setViewing(null)} /> : null}
    </>
  );
}

/**
 * The instant, in the building's timezone.
 *
 * `<bdi>` because the timestamp is a run of Latin digits and punctuation inside
 * a page that may be right-to-left; without it the bidirectional algorithm
 * reorders the parts and the date reads back to front.
 */
function Occurred({ at }: { at: string }): ReactElement {
  const { i18n } = useTranslation();
  const formatted = new Date(at).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: 'Asia/Riyadh',
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return <bdi>{formatted}</bdi>;
}

/**
 * "Name (Role)", exactly as the prototype composed it — and the role is the one
 * held *at the time*, not the one held now (REQ-077). An entry whose actor has
 * since been promoted must still say what they were when they acted.
 */
function Actor({ entry }: { entry: AuditEvent }): ReactElement {
  const { t } = useTranslation();

  if (!entry.actor_name) return <span className="hint">{t('sec_system')}</span>;
  if (!entry.actor_role) return <bdi>{entry.actor_name}</bdi>;

  /*
   * Falls back to the stored value when there is no label for it. The role is a
   * *snapshot*, so the log can hold roles that no longer exist — `hr` was
   * retired under ASM-07 and its entries must still say "HR", not blank.
   */
  const key = `role_${entry.actor_role}`;
  const label = t(key);

  /*
   * Each run isolated, rather than one composed string. "Layla A." is Latin
   * text inside an Arabic paragraph, and the bidirectional algorithm moves its
   * trailing full stop to the front — the name renders as ".Layla A". `<bdi>`
   * is what tells the algorithm where one direction ends and the next begins.
   */
  return (
    <>
      <bdi>{entry.actor_name}</bdi> (<bdi>{label === key ? entry.actor_role : label}</bdi>)
    </>
  );
}

interface PagerProps {
  page: number;
  totalPages: number;
  busy: boolean;
  onGo: (page: number) => void;
}

/**
 * Previous / position / next.
 *
 * The first list in the application that needs one: everything else fits in a
 * single page of a few hundred rows, and the log does not. Deliberately not a
 * numbered pager — with an append-only log the useful moves are "further back"
 * and "back to the top", and page 43 means nothing to anyone.
 */
function Pager({ page, totalPages, busy, onGo }: PagerProps): ReactElement {
  const { t } = useTranslation();

  return (
    <nav className="pager" aria-label={t('pg_label')}>
      <button
        type="button"
        className="btn sm"
        onClick={() => onGo(page - 1)}
        disabled={page <= 1 || busy}
      >
        {t('pg_prev')}
      </button>

      {/*
        `aria-live` so the position is announced after a page change. Without it
        a screen-reader user presses Next and is told nothing at all.
      */}
      <span className="pg-pos" aria-live="polite">
        {t('pg_position', { page, total: totalPages })}
      </span>

      <button
        type="button"
        className="btn sm"
        onClick={() => onGo(page + 1)}
        disabled={page >= totalPages || busy}
      >
        {t('pg_next')}
      </button>
    </nav>
  );
}

function EventDialog({ entry, onClose }: { entry: AuditEvent; onClose: () => void }): ReactElement {
  const { t } = useTranslation();

  return (
    <Modal
      title={entry.event}
      onClose={onClose}
      wide
      footer={
        <button type="button" className="btn ghost" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <dl className="dl">
        <dt>{t('sec_time')}</dt>
        <dd>
          <Occurred at={entry.occurred_at} />
        </dd>

        <dt>{t('sec_actor')}</dt>
        <dd>
          <Actor entry={entry} />
        </dd>

        {entry.target_type ? (
          <>
            <dt>{t('sec_target')}</dt>
            <dd>
              <bdi>
                {entry.target_type}
                {entry.target_id ? ` · ${entry.target_id}` : ''}
              </bdi>
            </dd>
          </>
        ) : null}

        {entry.ip ? (
          <>
            <dt>{t('sec_ip')}</dt>
            <dd>
              <bdi>{entry.ip}</bdi>
            </dd>
          </>
        ) : null}

        {entry.user_agent ? (
          <>
            <dt>{t('sec_agent')}</dt>
            <dd>
              <bdi>{entry.user_agent}</bdi>
            </dd>
          </>
        ) : null}
      </dl>
    </Modal>
  );
}
